import { Component, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { UserRights } from '@maximizer/core/shared/domain';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import {
  OnboardingStepNumber,
  PermissionMessage,
  StepGuideId,
  TeamMemberService,
  VerticalService,
  getPermissionString,
} from '@maximizer/outlook/shared/data-access';
import {
  EmailEntryType,
  EntryType,
  OutlookEmail,
} from '@maximizer/outlook/shared/domain';
import {
  OutlookAvatarClassesPipe,
  OutlookAvatarLettersPipe,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'maximizer-outlook-read-entry-row',
  templateUrl: './entry-row.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KENDO_BUTTONS,
    KENDO_TOOLTIPS,
    OutlookAvatarClassesPipe,
    OutlookAvatarLettersPipe,
    TranslatePipe,
    UI_PIPES,
  ],
})
export class OutlookReadEntryRowComponent {
  private readonly translate = inject(TranslateService);
  private readonly verticalService = inject(VerticalService);
  private readonly teamMemberService = inject(TeamMemberService);

  entry = input.required<OutlookEmail>();
  canNavigate = input<boolean>(true);
  isFromPopup = input<boolean>(false);
  index = input<number>(0);

  onboardingInProgress = input<boolean>(false);
  currentGuide = input<StepGuideId | undefined>(undefined);
  currentStep = input<OnboardingStepNumber | undefined>(undefined);
  entryActionsGuideVisible = input<boolean>(false);
  inMaximizerLength = input<number>(0);

  userRights = input<UserRights | undefined>(undefined);
  userEmails = input<Set<string>>(new Set());
  autoSaveEnabledIds = input<string[]>([]);

  navigate = output<OutlookEmail>();
  save = output<{ entry: OutlookEmail; fromPopup: boolean }>();

  readonly EntryType = EntryType;
  readonly StepGuideId = StepGuideId;
  readonly OnboardingStepNumber = OnboardingStepNumber;

  viewEntryTitle = computed(() => {
    const dataItem = this.entry();
    if (!this.canNavigate()) return null;

    if (dataItem.type === EntryType.Lead) {
      return this.translate.instant('outlook.main.tooltip.view-entry', {
        type: this.translate.instant('outlook.entries.lead'),
      });
    }

    if (dataItem.abentryType === EntryType.Individual) {
      return this.translate.instant('outlook.main.tooltip.view-entry', {
        type: this.verticalService.translateVertical('individual'),
      });
    }

    return this.translate.instant('outlook.main.tooltip.view-entry', {
      type: this.translate.instant('outlook.entries.contact'),
    });
  });

  entryTypeLabel = computed(() => {
    const dataItem = this.entry();
    const isTeam = this.isTeamMember(dataItem.emailAddress);
    const teamPrefix = isTeam
      ? this.translate.instant('outlook.shared.labels.teamMember') + ' / '
      : '';

    if (dataItem.type === EntryType.Lead) {
      return teamPrefix + this.translate.instant('outlook.entries.lead');
    }

    if (dataItem.abentryType === EntryType.Individual) {
      return teamPrefix + this.verticalService.translateVertical('individual');
    }

    return (
      teamPrefix +
      this.translate.instant('outlook.entries.' + dataItem.abentryType)
    );
  });

  autoSaveEnabled = computed(() => {
    const id = this.entry().id;
    if (!id) return false;
    return this.autoSaveEnabledIds().includes(id);
  });

  isTeamMember(email: string): boolean {
    return this.teamMemberService.isTeamMember(email);
  }

  isUser(email: string): boolean {
    return this.userEmails().has(email.toLowerCase());
  }

  getSaveEmailPermission(type?: EmailEntryType): boolean {
    const rights = this.userRights();
    if (!type) {
      return !!rights?.documents?.insert;
    }
    if (
      (type === EntryType.AbEntry && !rights?.addressBook.update) ||
      (type === EntryType.Lead && !rights?.lead.update) ||
      !rights?.documents.insert
    ) {
      return false;
    }
    return true;
  }

  getSaveEmailPermissionMessage(
    type: EmailEntryType | undefined,
    message: PermissionMessage,
  ): string {
    const rights = this.userRights();
    const getPermission = (
      entryType: EmailEntryType | 'document' | undefined,
      action: 'edit' | 'create' | undefined,
    ) => getPermissionString(this.translate, entryType, action, message);

    if (type === EntryType.AbEntry && !rights?.addressBook?.update) {
      return getPermission('abentry', 'edit');
    }
    if (type === EntryType.Lead && !rights?.lead?.update) {
      return getPermission('lead', 'edit');
    }
    if (!rights?.documents?.insert) {
      return getPermission('document', 'create');
    }
    return getPermission(undefined, undefined);
  }

  onNavigate(): void {
    if (!this.canNavigate()) return;
    this.navigate.emit(this.entry());
  }

  onSave(): void {
    this.save.emit({ entry: this.entry(), fromPopup: this.isFromPopup() });
  }
}
